import type { JSX } from 'react'
import { LineChart } from '@mantine/charts'
import {
  dotStyleFor,
  formatChartNumber,
  formatChartReading,
  formatUtcDate,
  hasLabSignals,
  normalizeCartesianData,
  normalizeTimeData,
  paddedYDomain,
  REFERENCE_HIGH_KEY,
  REFERENCE_LOW_KEY,
  type ChartDatum,
  type LabDatum,
} from './trend-chart.utils'

// Black value line between the blue (low) / red (high) reference threshold lines.
const SERIES_COLOR = 'black'

type DotRenderProps = {
  cx?: number
  cy?: number
  index?: number
  payload?: unknown
}

// Custom Recharts dot: reads the point's `abnormalFlag` and renders the matching shape/colour.
// Passed to the value line via Mantine's `lineProps`.
const AbnormalDot = ({ cx, cy, index, payload }: DotRenderProps): JSX.Element => {
  if (typeof cx !== 'number' || typeof cy !== 'number') return <g key={`dot-${index ?? 'na'}`} />
  const flag = (payload as LabDatum | undefined)?.abnormalFlag
  const { shape, color, radius } = dotStyleFor(flag)
  const common = { fill: color, stroke: 'white', strokeWidth: 1 }

  if (shape === 'circle') {
    return <circle key={`dot-${index ?? 'na'}`} cx={cx} cy={cy} r={radius} {...common} />
  }
  const r = radius
  const points =
    shape === 'triangle-up'
      ? `${cx},${cy - r} ${cx - r},${cy + r} ${cx + r},${cy + r}`
      : shape === 'triangle-down'
        ? `${cx},${cy + r} ${cx - r},${cy - r} ${cx + r},${cy - r}`
        : `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`
  return <polygon key={`dot-${index ?? 'na'}`} points={points} {...common} />
}

// A categorical x value that is actually a date (a single-point series falls back to the category
// axis — see isTimeAxisData's two-point guard) still renders as MM/DD/YYYY instead of leaking the
// raw ISO timestamp string.
const formatCategoryLabel = (value: unknown): string => {
  if (typeof value === 'string' && /\d{4}/.test(value)) {
    const ms = Date.parse(value)
    if (!Number.isNaN(ms)) return formatUtcDate(ms)
  }
  return typeof value === 'string' || typeof value === 'number' ? String(value) : ''
}

const REFERENCE_KEYS = new Set<string>([REFERENCE_HIGH_KEY, REFERENCE_LOW_KEY])
const REFERENCE_LABELS: Record<string, string> = {
  [REFERENCE_HIGH_KEY]: 'Reference high',
  [REFERENCE_LOW_KEY]: 'Reference low',
}

// One tooltip payload entry, as Recharts hands it to a custom content renderer.
type TooltipEntry = {
  dataKey?: string | number
  name?: string | number
  value?: number | string
  color?: string
  payload?: LabDatum
}

const TooltipRow = ({
  entry,
  label,
  comparator,
}: {
  entry: TooltipEntry
  label: string
  comparator?: LabDatum['comparator']
}): JSX.Element => (
  <div className="flex items-center gap-2.5">
    <span
      className="size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: entry.color ?? 'currentColor' }}
    />
    <span className="flex-1 pr-6 text-slate-700">{label}</span>
    <span className="font-medium tabular-nums">
      {typeof entry.value === 'number'
        ? formatChartReading(entry.value, comparator)
        : (entry.value ?? '')}
    </span>
  </div>
)

// Custom tooltip: same content as Mantine's default, but the value row is separated from the
// reference-threshold rows by a divider, and labelled with the measurement name when the caller
// provides one (instead of the raw series key, "value"). A qualified reading's value row keeps its
// comparator ('<5'); the reference rows are exact bounds.
export const TrendTooltip = ({
  label,
  payload,
  valueLabel,
}: {
  label: string
  payload: TooltipEntry[]
  valueLabel?: string
}): JSX.Element | null => {
  const rows = payload.filter((entry) => entry.value != null)
  if (rows.length === 0) return null
  const valueRows = rows.filter((entry) => !REFERENCE_KEYS.has(String(entry.dataKey)))
  const referenceRows = rows.filter((entry) => REFERENCE_KEYS.has(String(entry.dataKey)))

  // Slightly off-white background so the popup separates from the white chart card behind it.
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm shadow-md">
      <div className="mb-1.5 font-medium">{label}</div>
      <div className="flex flex-col gap-1">
        {valueRows.map((entry) => (
          <TooltipRow
            key={String(entry.dataKey)}
            entry={entry}
            label={valueLabel ?? String(entry.name ?? '')}
            comparator={entry.payload?.comparator}
          />
        ))}
        {valueRows.length > 0 && referenceRows.length > 0 && (
          <div className="my-1 border-t border-slate-200" />
        )}
        {referenceRows.map((entry) => (
          <TooltipRow
            key={String(entry.dataKey)}
            entry={entry}
            label={REFERENCE_LABELS[String(entry.dataKey)] ?? String(entry.name ?? '')}
          />
        ))}
      </div>
    </div>
  )
}

// Per-series Recharts props: threshold lines show no dots; the value line uses the abnormal-aware
// dot. Spread last by Mantine, so it overrides the defaults.
const labLineProps = (series: { name: string }) =>
  series.name === REFERENCE_HIGH_KEY || series.name === REFERENCE_LOW_KEY
    ? { dot: false, activeDot: false, strokeWidth: 1.5 }
    : { dot: AbnormalDot, activeDot: AbnormalDot }

type Props = {
  /** Chart points. Each may carry `referenceLow`/`referenceHigh`/`abnormalFlag` lab fields. */
  data: ReadonlyArray<ChartDatum>
  xKey: string
  yKey: string
  h?: number
  /**
   * X-axis scale. 'category' (default) spaces points evenly; 'time' parses x as dates and places
   * them on a numeric time axis, so horizontal gaps reflect the real elapsed time between readings.
   */
  xScale?: 'category' | 'time'
  /** Tick/tooltip label formatter for 'time' mode (receives epoch ms). Defaults to MM/DD/YYYY UTC. */
  xTickFormatter?: (ms: number) => string
  /** Measurement name for the tooltip's value row (e.g. "POCT Glucose (mg/dL)"); falls back to the series key. */
  valueLabel?: string
}

/**
 * Shared trend line chart. When the points carry lab signals (reference bounds or an abnormal
 * flag) it draws dashed high/low threshold lines and colours each point by its abnormal flag;
 * otherwise it renders a plain single line. Used by both the Assistant and the Clinical Summary.
 */
export const TrendLineChart = ({
  data,
  xKey,
  yKey,
  h = 260,
  xScale = 'category',
  xTickFormatter,
  valueLabel,
}: Props): JSX.Element => {
  const isTime = xScale === 'time'
  const cartesian = isTime
    ? normalizeTimeData(data, xKey, yKey)
    : normalizeCartesianData(data, xKey, yKey)
  const isLab = hasLabSignals(cartesian)
  const formatX = xTickFormatter ?? formatUtcDate
  // Zoom the y-axis to the plotted range (±10%) instead of Recharts' zero-anchored default.
  const yDomain = paddedYDomain(cartesian, yKey)

  // Lab charts gain dashed high/low threshold series (each tracking its own per-point reference
  // bound) plus the abnormal-aware dot; everything else keeps the plain single-line rendering.
  const series = [
    { name: yKey, color: SERIES_COLOR },
    ...(isLab && cartesian.some((d) => typeof (d as LabDatum).referenceHigh === 'number')
      ? [
          {
            name: REFERENCE_HIGH_KEY,
            label: 'Reference high',
            color: 'red.4',
            strokeDasharray: '4 4',
            curveType: 'stepAfter' as const,
          },
        ]
      : []),
    ...(isLab && cartesian.some((d) => typeof (d as LabDatum).referenceLow === 'number')
      ? [
          {
            name: REFERENCE_LOW_KEY,
            label: 'Reference low',
            color: 'indigo.4',
            strokeDasharray: '4 4',
            curveType: 'stepAfter' as const,
          },
        ]
      : []),
  ]

  return (
    <LineChart
      h={h}
      data={cartesian}
      dataKey={xKey}
      series={series}
      valueFormatter={formatChartNumber}
      curveType="linear"
      withDots
      tickLine="none"
      gridAxis="y"
      connectNulls={isLab ? false : undefined}
      lineProps={isLab ? labLineProps : undefined}
      yAxisProps={yDomain ? { domain: yDomain } : undefined}
      xAxisProps={
        isTime
          ? {
              type: 'number',
              scale: 'time',
              domain: ['dataMin', 'dataMax'],
              // Horizontal date labels; Recharts measures them and skips any that would
              // overlap (preserving the first and last), instead of rotating to fit.
              tickFormatter: (ms: number) => formatX(Number(ms)),
              interval: 'preserveStartEnd',
              minTickGap: 24,
            }
          : {
              tickFormatter: formatCategoryLabel,
              interval: 'preserveStartEnd',
              minTickGap: 24,
            }
      }
      tooltipProps={{
        content: ({ label, payload }) => (
          <TrendTooltip
            label={isTime ? formatX(Number(label)) : formatCategoryLabel(label)}
            payload={(payload ?? []) as unknown as TooltipEntry[]}
            valueLabel={valueLabel}
          />
        ),
      }}
    />
  )
}
